// Entrada do painel: query client, tempo real, avisos e as rotas. Telas pesadas (editor, dashboard, campanhas) entram
// por import dinâmico — a primeira carga precisa ser leve (TELAS §1).
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense, lazy, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import './estilo.css';
import { Layout } from './layout';
import { api, EVENTO_SAIU } from './nucleo/api';
import { ProvedorTempoReal } from './nucleo/tempo-real';
import { Entrar, type EstadoAuth } from './telas/entrar';
import { Esqueleto, ProvedorAvisos } from './ui';

const Inicio = lazy(() => import('./telas/inicio').then((m) => ({ default: m.Inicio })));
const Crm = lazy(() => import('./telas/crm').then((m) => ({ default: m.Crm })));
const Solicitacoes = lazy(() => import('./telas/solicitacoes').then((m) => ({ default: m.Solicitacoes })));
const Numeros = lazy(() => import('./telas/numeros').then((m) => ({ default: m.Numeros })));
const Agente = lazy(() => import('./telas/agente').then((m) => ({ default: m.Agente })));
const Dashboard = lazy(() => import('./telas/dashboard').then((m) => ({ default: m.Dashboard })));
const Saude = lazy(() => import('./telas/saude').then((m) => ({ default: m.Saude })));
const Leads = lazy(() => import('./telas/listas').then((m) => ({ default: m.Leads })));
const Assinaturas = lazy(() => import('./telas/listas').then((m) => ({ default: m.Assinaturas })));
const Cerebros = lazy(() => import('./telas/listas').then((m) => ({ default: m.Cerebros })));
const Relatos = lazy(() => import('./telas/listas').then((m) => ({ default: m.Relatos })));
const Campanhas = lazy(() => import('./telas/campanhas').then((m) => ({ default: m.Campanhas })));
const TestesAb = lazy(() => import('./telas/campanhas').then((m) => ({ default: m.TestesAb })));
const Configuracoes = lazy(() => import('./telas/config').then((m) => ({ default: m.Configuracoes })));
const Remarketing = lazy(() => import('./telas/remarketing').then((m) => ({ default: m.Remarketing })));
const Fluxos = lazy(() => import('./telas/fluxos').then((m) => ({ default: m.Fluxos })));
const Espionagem = lazy(() => import('./telas/espionagem').then((m) => ({ default: m.Espionagem })));
const Criativos = lazy(() => import('./telas/criativos').then((m) => ({ default: m.Criativos })));
const Planilha = lazy(() => import('./telas/planilha').then((m) => ({ default: m.Planilha })));
const ConferirExtrato = lazy(() => import('./telas/extrato').then((m) => ({ default: m.ConferirExtrato })));
const EditorFluxo = lazy(() => import('./telas/fluxos').then((m) => ({ default: m.EditorFluxo })));

// DEPOIS DE UM DEPLOY a aba que ficou aberta pede os pedaços da versão anterior (os nomes mudam a cada build) e a tela
// travava no "carregando" (22/10, CRM). O Vite avisa quando um import falha: recarrega UMA vez (no máximo a cada 20 s, pra
// nunca entrar em laço se o servidor estiver fora).
window.addEventListener('vite:preloadError', (e) => {
  const ultima = Number(sessionStorage.getItem('nexus_recarregou') || 0);
  if (Date.now() - ultima < 20_000) return;
  e.preventDefault();
  sessionStorage.setItem('nexus_recarregou', String(Date.now()));
  location.reload();
});

const cliente = new QueryClient({
  defaultOptions: {
    queries: {
      // o websocket é quem avisa que mudou; o refetch é só rede de segurança
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

function App() {
  const [estado, setEstado] = useState<EstadoAuth | null>(null);

  useEffect(() => {
    void api
      .get<EstadoAuth>('/auth/estado')
      .then(setEstado)
      .catch(() => setEstado({ configurado: true, entrou: false, usuario: null }));
  }, []);

  // 09/10/2026 (achado 39): "sair" e sessão que caiu voltam pro login. Antes o `entrou` nunca voltava a false: o sair
  // navegava pra /entrar, que não existe pra quem entrou, e caía no Início com tudo dando 401 até um F5.
  useEffect(() => {
    const saiu = (): void => setEstado((e) => (e?.entrou ? { configurado: true, entrou: false, usuario: null } : e));
    window.addEventListener(EVENTO_SAIU, saiu);
    return () => window.removeEventListener(EVENTO_SAIU, saiu);
  }, []);

  if (!estado) {
    return (
      <div className="p-8">
        <Esqueleto linhas={4} />
      </div>
    );
  }

  if (!estado.entrou) {
    return (
      <Routes>
        <Route path="*" element={<Entrar estado={estado} aoEntrar={(usuario) => setEstado({ configurado: true, entrou: true, usuario })} />} />
      </Routes>
    );
  }

  return (
    <ProvedorTempoReal>
      <Suspense
        fallback={
          <div className="p-8">
            <Esqueleto linhas={5} />
          </div>
        }
      >
        <Routes>
          <Route element={<Layout usuario={estado.usuario} />}>
            <Route index element={<Inicio />} />
            <Route path="crm" element={<Crm />} />
            <Route path="crm/:id" element={<Crm />} />
            <Route path="solicitacoes" element={<Solicitacoes />} />
            <Route path="numeros" element={<Numeros />} />
            <Route path="agente" element={<Agente />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="saude" element={<Saude />} />
            <Route path="leads" element={<Leads />} />
            <Route path="assinaturas" element={<Assinaturas />} />
            <Route path="cerebros" element={<Cerebros />} />
            <Route path="relatos" element={<Relatos />} />
            <Route path="campanhas" element={<Campanhas />} />
            <Route path="espionagem" element={<Espionagem />} />
            <Route path="criativos" element={<Criativos />} />
            <Route path="planilha" element={<Planilha />} />
            <Route path="extrato" element={<ConferirExtrato />} />
            <Route path="ab" element={<TestesAb />} />
            <Route path="config" element={<Configuracoes />} />
            <Route path="remarketing" element={<Remarketing />} />
            <Route path="fluxos" element={<Fluxos />} />
            <Route path="fluxos/:id" element={<EditorFluxo />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </ProvedorTempoReal>
  );
}

createRoot(document.getElementById('raiz')!).render(
  <StrictMode>
    <QueryClientProvider client={cliente}>
      <ProvedorAvisos>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ProvedorAvisos>
    </QueryClientProvider>
  </StrictMode>,
);
